import { Amount, Price, Token, USD_TOKEN } from "@eulerxyz/core-sdk"
import { createSelector } from "@reduxjs/toolkit"
import {
  type Address,
  getAddress,
  isAddress,
  maxUint256,
  zeroAddress,
} from "viem"
import type { GetUnitOfAccountPriceData, GetVaultData } from "../query"
import type { VaultResults } from "../utils"
import { isBorrowableCollateral } from "../utils/vaultUtils"

/**
 * Converts amounts that are in terms of the underlying asset into the price of the oracle (USD).
 * This selector takes an amount of the underlying asset, converts it to the unit of account,
 * and then to USD using the oracle price.
 * @deprecated We should use the price from the api instead!
 */
export const selectPrice = createSelector(
  [
    (amountIn: bigint) => amountIn,
    (_, vault: GetVaultData) => vault,
    (_, __, price: GetUnitOfAccountPriceData) => price,
  ],
  (amountIn, vault, price = 0n) => {
    if (!vault || !price || !vault?.liabilityPriceInfo || amountIn === 0n)
      return null
    if (amountIn === maxUint256) {
      // console.warn("Max Value calculation: ", vault.vault);
      return null
    }
    const unitOfAccount = new Token({
      decimals: vault.unitOfAccountDecimals.toString(),
      name: vault.unitOfAccountName,
      address: vault.unitOfAccount,
      symbol: vault.unitOfAccountSymbol,
    })

    const asset = new Token({
      decimals: vault.assetDecimals.toString(),
      name: vault.assetName,
      address: vault.asset,
      symbol: vault.assetSymbol,
    })

    // Price of unit of account in USD
    const oraclePrice = new Price(
      unitOfAccount,
      USD_TOKEN,
      10n ** vault.unitOfAccountDecimals,
      price,
    )

    // Use backup pricing if liability pricing query failed
    const liabilityPricing = vault.liabilityPriceInfo.queryFailure
      ? vault.backupAssetPriceInfo
      : vault.liabilityPriceInfo

    if (!liabilityPricing.amountIn || !liabilityPricing.amountOutBid)
      return null
    // Price of asset in terms of unit of account
    const tokenPrice = new Price(
      asset,
      unitOfAccount,
      liabilityPricing.amountIn,
      liabilityPricing.amountOutMid,
    )

    // Convert asset amount to unit of account, then to USD
    try {
      const value = oraclePrice.quote(
        tokenPrice.quote(Amount.fromRawAmount(asset, amountIn)),
      )

      return value
    } catch (e) {
      console.error("[selectPrice] error: ", (e as Error).message)

      return null
    }
  },
)

export const selectPriceByLiability = createSelector(
  [
    (amountIn: bigint) => amountIn,
    (_, liabilityVault: GetVaultData) => liabilityVault,
    (_, __, collateralVault: GetVaultData) => collateralVault,
    (_, __, ___, price: GetUnitOfAccountPriceData) => price,
  ],
  (amountIn, vault, collateralVault, price) => {
    if (!vault || !price || !collateralVault) return null
    const unitOfAccount = new Token({
      decimals: vault.unitOfAccountDecimals.toString(),
      name: vault.unitOfAccountName,
      address: vault.unitOfAccount,
      symbol: vault.unitOfAccountSymbol,
    })

    const asset = new Token({
      decimals: vault.assetDecimals.toString(),
      name: vault.assetName,
      address: vault.asset,
      symbol: vault.assetSymbol,
    })

    // Price of unit of account in USD
    const oraclePrice = new Price(
      unitOfAccount,
      USD_TOKEN,
      10n ** vault.unitOfAccountDecimals,
      price,
    )

    // Use backup pricing if liability pricing query failed
    const liabilityPricing = vault.collateralPriceInfo?.find(
      (info) =>
        isAddress(info.asset) &&
        isAddress(collateralVault.vault) &&
        getAddress(info.asset) === getAddress(collateralVault.vault),
    )
    if (!liabilityPricing?.amountIn || !liabilityPricing?.amountOutBid)
      return null

    // Price of asset in terms of unit of account
    const tokenPrice = new Price(
      asset,
      unitOfAccount,
      liabilityPricing.amountIn,
      liabilityPricing.amountOutMid,
    )

    // Convert asset amount to unit of account, then to USD
    return oraclePrice.quote(
      tokenPrice.quote(Amount.fromRawAmount(asset, amountIn)),
    )
  },
)

export const selectPriceFromPriceApi = createSelector(
  [
    (amount: bigint) => amount,
    (_, decimals?: bigint) => decimals,
    (_, __, pricePerToken?: bigint) => pricePerToken,
  ],
  (amount, decimals = 18n, pricePerToken = 0n) => {
    const tokenIn = new Token({
      address: zeroAddress,
      decimals: decimals.toString(),
    })

    const dollarPrice = new Price(
      tokenIn,
      USD_TOKEN,
      10n ** decimals,
      pricePerToken,
    )

    return dollarPrice.quote(Amount.fromRawAmount(tokenIn, amount))
  },
)

export const selectPriceWithFallback = createSelector(
  [
    (amountAssets: bigint) => amountAssets,
    (_, vault: GetVaultData) => vault,
    (_, __, allUnitOfAccountPrices: Map<Address, bigint>) =>
      allUnitOfAccountPrices,
  ],
  (amountAssets, vault, allUnitOfAccountPrices): Amount<Token> | null => {
    if (!vault) return null

    const canBeBorrowed =
      vault.collateralLTVInfo.filter((ltvInfo) =>
        isBorrowableCollateral(ltvInfo),
      ).length > 0

    const liabilityPricing = vault.liabilityPriceInfo?.queryFailure
      ? vault.backupAssetPriceInfo
      : vault.liabilityPriceInfo

    const hasLiabilityPricing =
      !!liabilityPricing?.amountIn && !!liabilityPricing?.amountOutBid

    const assetApiPrice = allUnitOfAccountPrices.get(getAddress(vault.asset))

    const shouldUseApiPrice = !canBeBorrowed || !hasLiabilityPricing

    return shouldUseApiPrice && assetApiPrice
      ? selectPriceFromPriceApi(
          amountAssets,
          vault.assetDecimals,
          assetApiPrice,
        )
      : selectPrice(
          amountAssets,
          vault,
          allUnitOfAccountPrices.get(vault.unitOfAccount) ?? 0n,
        )
  },
)

/**
 * Converts an amount in unit of account to its equivalent USD price using the oracle price.
 * This selector should be used when converting values that are in terms of unit of account to the oracle price (USD).
 */
export const selectConvertUnitOfAccountAmountToPrice = createSelector<
  [
    (amountInUnitOfAccount: bigint) => bigint,
    (
      amountInUnitOfAccount: bigint,
      vault: VaultResults | GetVaultData,
    ) => VaultResults | GetVaultData,
    (
      amountInUnitOfAccount: bigint,
      vault: VaultResults | GetVaultData,
      allUnitOfAccountPrices: Map<Address, bigint>,
    ) => Map<Address, bigint>,
  ],
  Amount<Token> | null
>(
  [
    (amountInUnitOfAccount: bigint) => amountInUnitOfAccount,
    (_, vault: VaultResults | GetVaultData) => vault,
    (_, __, allUnitOfAccountPrices: Map<Address, bigint>) =>
      allUnitOfAccountPrices,
  ],
  (
    amountInUnitOfAccount,
    vault,
    allUnitOfAccountPrices,
  ): Amount<Token> | null => {
    if (!vault || allUnitOfAccountPrices === undefined) return null

    const unitOfAccount = new Token({
      decimals: vault.unitOfAccountDecimals.toString(),
      name: vault.unitOfAccountName,
      address: vault.unitOfAccount,
      symbol: vault.unitOfAccountSymbol,
    })

    const usdToken = USD_TOKEN

    const unitOfAccountWithBackup =
      vault?.unitOfAccount === zeroAddress
        ? vault.backupAssetPriceInfo.unitOfAccount
        : vault.unitOfAccount

    const unitOfAccountPriceFallback = allUnitOfAccountPrices.get(
      getAddress(unitOfAccountWithBackup),
    )

    // Create a Price object to represent the exchange rate between unit of account and USD
    const exchangeRate = new Price(
      unitOfAccount,
      usdToken,
      10n ** BigInt(vault.unitOfAccountDecimals),
      unitOfAccountPriceFallback ?? 0n,
    )

    // Convert the input amount to an Amount object in terms of unit of account
    const inputAmount = Amount.fromRawAmount(
      unitOfAccount,
      amountInUnitOfAccount,
    )

    // Use the quote method to convert the input amount from unit of account to USD
    return exchangeRate.quote(inputAmount)
  },
)

export const selectUnitOfAccountWithBackup = createSelector(
  [(vault: GetVaultData) => vault],
  (vault): Address => {
    if (!vault) return zeroAddress
    return vault.unitOfAccount === zeroAddress
      ? vault.backupAssetPriceInfo.unitOfAccount
      : vault.unitOfAccount
  },
)

export interface OraclePriceFromUnitOfAccountResult {
  unitOfAccountWithBackup: Address
  oraclePriceNumber: number | null
  oraclePriceBigInt: bigint | null
  oraclePrice: Price<Token, Token> | null
}

export const selectOraclePriceFromUnitOfAccount = createSelector(
  [
    (vault: GetVaultData) => vault,
    (_, allUnitOfAccountPrices: Map<Address, bigint>) => allUnitOfAccountPrices,
  ],
  (vault, allUnitOfAccountPrices): OraclePriceFromUnitOfAccountResult => {
    if (!vault)
      return {
        unitOfAccountWithBackup: zeroAddress,
        oraclePriceNumber: null,
        oraclePriceBigInt: null,
        oraclePrice: null,
      }

    const unitOfAccountWithBackup =
      vault?.unitOfAccount === zeroAddress
        ? vault.backupAssetPriceInfo.unitOfAccount
        : vault.unitOfAccount

    const price = allUnitOfAccountPrices.get(
      getAddress(unitOfAccountWithBackup),
    )
    if (!price)
      return {
        unitOfAccountWithBackup,
        oraclePriceNumber: null,
        oraclePriceBigInt: null,
        oraclePrice: null,
      }

    const unitOfAccount = new Token({
      decimals: vault.unitOfAccountDecimals.toString(),
      name: vault.unitOfAccountName,
      address: vault.unitOfAccount, //? unitOfAccountWithBackup
      symbol: vault.unitOfAccountSymbol,
    })
    const oraclePrice = new Price(
      unitOfAccount,
      USD_TOKEN,
      10n ** vault.unitOfAccountDecimals,
      price,
    )
    return {
      unitOfAccountWithBackup,
      oraclePriceNumber: Number(oraclePrice.toSignificant(18)),
      oraclePriceBigInt: price,
      oraclePrice,
    }
  },
)

export interface SelectPriceExtendedResult {
  amountIn: bigint | undefined
  error: string | null
  amountOutUSDPrice: Amount<Token> | null
  amountOutUSDFullScale: string | null
  amountOutUSDFullScaleNumber: number | null
  oraclePriceNumber: number | null
  oraclePriceBigInt: bigint | null
  unitOfAccountWithBackup: Address
}

export const selectPriceExtended = createSelector(
  [
    (amountIn: bigint | undefined) => amountIn,
    (_, vault: GetVaultData) => vault,
    (_, __, allUnitOfAccountPrices: Map<Address, bigint>) =>
      allUnitOfAccountPrices,
  ],
  (amountIn, vault, allUnitOfAccountPrices): SelectPriceExtendedResult => {
    if (amountIn === undefined) amountIn = 0n
    try {
      if (!vault) {
        return {
          amountIn,
          error: "No vault provided",
          amountOutUSDPrice: null,
          amountOutUSDFullScale: null,
          amountOutUSDFullScaleNumber: null,
          oraclePriceNumber: null,
          oraclePriceBigInt: null,
          unitOfAccountWithBackup: zeroAddress,
        }
      }

      const { unitOfAccountWithBackup, oraclePriceBigInt, oraclePriceNumber } =
        selectOraclePriceFromUnitOfAccount(vault, allUnitOfAccountPrices)

      if (!vault || !oraclePriceBigInt)
        return {
          amountIn,
          error: "No price found",
          amountOutUSDPrice: null,
          amountOutUSDFullScale: null,
          amountOutUSDFullScaleNumber: null,
          oraclePriceNumber: null,
          oraclePriceBigInt: null,
          unitOfAccountWithBackup,
        }
      const amountOutUSDPrice = selectPrice(amountIn, vault, oraclePriceBigInt)
      if (!amountOutUSDPrice) {
        return {
          amountIn,
          error: "No amount out USD price",
          amountOutUSDPrice: null,
          amountOutUSDFullScale: null,
          amountOutUSDFullScaleNumber: null,
          oraclePriceNumber: null,
          oraclePriceBigInt: null,
          unitOfAccountWithBackup,
        }
      }

      const amountOutFullScale = amountOutUSDPrice.toSignificant(
        amountOutUSDPrice.currency.decimals,
      )
      return {
        amountIn,
        amountOutUSDPrice: amountOutUSDPrice,
        amountOutUSDFullScale: amountOutFullScale,
        amountOutUSDFullScaleNumber: Number(amountOutFullScale),
        oraclePriceNumber,
        oraclePriceBigInt,
        unitOfAccountWithBackup,
        error: null,
      }
    } catch (e) {
      console.error(e)
      return {
        amountIn,
        error: "No vault provided",
        amountOutUSDPrice: null,
        amountOutUSDFullScale: null,
        amountOutUSDFullScaleNumber: null,
        oraclePriceNumber: null,
        oraclePriceBigInt: null,
        unitOfAccountWithBackup: zeroAddress,
      }
    }
  },
)

export const selectPriceExtendedByLiability = createSelector(
  [
    (amountIn: bigint | undefined) => amountIn,
    (_, collateralVault: GetVaultData) => collateralVault,
    (_, __, liabilityVault: GetVaultData) => liabilityVault,
    (_, __, ___, allUnitOfAccountPrices: Map<Address, bigint>) =>
      allUnitOfAccountPrices,
  ],
  (
    amountIn,
    collateralVault,
    liabilityVault,
    allUnitOfAccountPrices,
  ): SelectPriceExtendedResult => {
    if (amountIn === undefined) amountIn = 0n
    try {
      if (!liabilityVault) {
        return {
          amountIn,
          error: "No vault provided",
          amountOutUSDPrice: null,
          amountOutUSDFullScale: null,
          amountOutUSDFullScaleNumber: null,
          oraclePriceNumber: null,
          oraclePriceBigInt: null,
          unitOfAccountWithBackup: zeroAddress,
        }
      }

      const { unitOfAccountWithBackup, oraclePriceBigInt, oraclePriceNumber } =
        selectOraclePriceFromUnitOfAccount(
          liabilityVault,
          allUnitOfAccountPrices,
        )

      if (!liabilityVault || !oraclePriceBigInt)
        return {
          amountIn,
          error: "No price found",
          amountOutUSDPrice: null,
          amountOutUSDFullScale: null,
          amountOutUSDFullScaleNumber: null,
          oraclePriceNumber: null,
          oraclePriceBigInt: null,
          unitOfAccountWithBackup,
        }

      const amountOutUSDPrice = selectPriceByLiability(
        amountIn,
        liabilityVault,
        collateralVault,
        oraclePriceBigInt,
      )
      if (!amountOutUSDPrice) {
        return {
          amountIn,
          error: "No amount out USD price",
          amountOutUSDPrice: null,
          amountOutUSDFullScale: null,
          amountOutUSDFullScaleNumber: null,
          oraclePriceNumber: null,
          oraclePriceBigInt: null,
          unitOfAccountWithBackup,
        }
      }

      const amountOutFullScale = amountOutUSDPrice.toSignificant(
        amountOutUSDPrice.currency.decimals,
      )
      return {
        amountIn,
        amountOutUSDPrice: amountOutUSDPrice,
        amountOutUSDFullScale: amountOutFullScale,
        amountOutUSDFullScaleNumber: Number(amountOutFullScale),
        oraclePriceNumber,
        oraclePriceBigInt,
        unitOfAccountWithBackup,
        error: null,
      }
    } catch (e) {
      console.error(e)
      return {
        amountIn,
        error: "No vault provided",
        amountOutUSDPrice: null,
        amountOutUSDFullScale: null,
        amountOutUSDFullScaleNumber: null,
        oraclePriceNumber: null,
        oraclePriceBigInt: null,
        unitOfAccountWithBackup: zeroAddress,
      }
    }
  },
)

/**
 * Converts amounts in unit of account to the asset value
 */
export const selectUnitOfAccountAmountToAsset = createSelector(
  [
    (amountIn: bigint) => amountIn,
    (_, vault: GetVaultData) => vault,
    (_, __, price: GetUnitOfAccountPriceData) => price,
  ],
  (amountIn, vault, price) => {
    if (!vault || !price || !vault?.liabilityPriceInfo) return null

    const asset = new Token({
      decimals: vault.assetDecimals.toString(),
      name: vault.assetName,
      address: vault.asset,
      symbol: vault.assetSymbol,
    })

    // Use backup pricing if liability pricing query failed
    const liabilityPricing = vault.liabilityPriceInfo.queryFailure
      ? vault.backupAssetPriceInfo
      : vault.liabilityPriceInfo

    if (!liabilityPricing.amountIn || !liabilityPricing.amountOutBid)
      return null

    // Convert from unit of account to asset value
    const amount =
      amountIn && vault
        ? (amountIn * 10n ** vault.assetDecimals) /
          (liabilityPricing.amountOutMid || 1n)
        : 0n

    return Amount.fromRawAmount(asset, amount)
  },
)
